"use client"

import { useState, useEffect, useCallback } from "react"
import { useAuth0 } from "@auth0/auth0-react"
import { parseApiError } from "../../lib/apiErrors"

export const useFetchActions = ({ groupId, campaignId, initialPage = 1, initialPageSize = 10 }) => {
  const [actions, setActions] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState([])
  const [sortBy, setSortBy] = useState("createdOn")
  const [sortOrder, setSortOrder] = useState("desc")
  const [currentPage, setCurrentPage] = useState(initialPage)
  const [totalPages, setTotalPages] = useState(1)
  const [pageSize, setPageSize] = useState(initialPageSize)
  const { getAccessTokenSilently } = useAuth0()
  const apiUrl = process.env.REACT_APP_API_URL

  // Modify the fetchActions function to better handle the API response
  const fetchActions = useCallback(async () => {
    try {
      setLoading(true)
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_API_AUDIENCE,
        },
      })

      // Build the URL with query parameters
      let url = `${apiUrl}/api/actions/getActions/${groupId}`

      // Add query parameters
      const queryParams = new URLSearchParams()
      queryParams.append("page", currentPage.toString())
      queryParams.append("pageSize", pageSize.toString())
      queryParams.append("sortBy", sortBy)
      queryParams.append("sortOrder", sortOrder)

      if (campaignId) {
        queryParams.append("campaignId", campaignId)
      }

      if (searchTerm) {
        queryParams.append("search", searchTerm)
      }

      if (statusFilter.length > 0) {
        queryParams.append("statuses", statusFilter.join(","))
      }

      // Append query parameters to URL
      url = `${url}?${queryParams.toString()}`

      console.log("Fetching from URL:", url)

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        throw await parseApiError(response, `Failed to fetch actions (${response.status})`)
      }

      const data = await response.json()
      console.log("API Response:", data)

      // Handle different response structures
      let actionsList = []
      let totalPagesCount = 1

      if (data && Array.isArray(data)) {
        // Case 1: API returns an array directly
        actionsList = data
        totalPagesCount = Math.ceil(data.length / pageSize)
      } else if (data && typeof data === "object") {
        if (Array.isArray(data.items)) {
          // Case 2: API returns an object with items property
          actionsList = data.items
          totalPagesCount = data.totalPages || Math.ceil(data.items.length / pageSize)
        } else if (Array.isArray(data.data)) {
          // Case 3: API returns an object with data property
          actionsList = data.data
          totalPagesCount = data.totalPages || Math.ceil(data.data.length / pageSize)
        } else if (Array.isArray(data.actions)) {
          // Case 4: API returns an object with actions property
          actionsList = data.actions
          totalPagesCount = data.totalPages || Math.ceil(data.actions.length / pageSize)
        } else if (data.id) {
          // Case 5: API returns a single action object
          actionsList = [data]
          totalPagesCount = 1
        } else {
          // Case 6: API returns an object with unknown structure
          // Try to find an array property
          const arrayProps = Object.keys(data).filter((key) => Array.isArray(data[key]))
          if (arrayProps.length > 0) {
            actionsList = data[arrayProps[0]]
            totalPagesCount = data.totalPages || Math.ceil(actionsList.length / pageSize)
          } else {
            console.error("Could not find an array in the API response:", data)
            actionsList = []
            totalPagesCount = 1
          }
        }
      }

      console.log("Processed actions list:", actionsList)
      setActions(actionsList)
      setTotalPages(totalPagesCount)
      setError(null)
    } catch (err) {
      console.error("Error fetching actions:", err)
      setError(err.message)
      setActions([])
    } finally {
      setLoading(false)
    }
  }, [
    apiUrl,
    getAccessTokenSilently,
    groupId,
    campaignId,
    currentPage,
    pageSize,
    sortBy,
    sortOrder,
    searchTerm,
    statusFilter,
  ])

  useEffect(() => {
    if (groupId) {
      fetchActions()
    }
  }, [fetchActions, groupId])

  // Handler functions
  const handleSearch = useCallback((term) => {
    setSearchTerm(term)
    setCurrentPage(1) // Reset to first page on new search
  }, [])

  const handleStatusFilterChange = useCallback((status) => {
    setStatusFilter((prev) => {
      if (prev.includes(status)) {
        return prev.filter((s) => s !== status)
      } else {
        return [...prev, status]
      }
    })
  }, [])

  const handleSortChange = useCallback(
    (field) => {
      if (sortBy === field) {
        setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"))
      } else {
        setSortBy(field)
        setSortOrder("asc")
      }
    },
    [sortBy],
  )

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page)
  }, [])

  const handlePageSizeChange = useCallback((size) => {
    setPageSize(size)
    setCurrentPage(1) // Reset to first page when changing page size
  }, [])

  const applyFilters = useCallback(() => {
    setCurrentPage(1) // Reset to first page when applying filters
    fetchActions()
  }, [fetchActions])

  return {
    actions,
    loading,
    error,
    searchTerm,
    statusFilter,
    sortBy,
    sortOrder,
    currentPage,
    totalPages,
    pageSize,
    handleSearch,
    handleStatusFilterChange,
    handleSortChange,
    handlePageChange,
    handlePageSizeChange,
    applyFilters,
    fetchActions,
  }
}
